import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Chip, Collapse, IconButton, Link, MenuItem, Pagination, TextField, ToggleButton, ToggleButtonGroup,
  useMediaQuery, useTheme,
  Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useAuth } from '../contexts/AuthContext';
import { SectionCard } from './SectionCard';
import { SectionSkeleton } from './SectionSkeleton';
import { formatMoney } from '../utils/money';
import { getPlatformName, getPlatformColor } from '../config/platforms';
import { FullTextTooltip } from './FullTextTooltip';

// DataGrid (~100 KB) loads only when the Table view is chosen
const BoostsTable = React.lazy(() => import('./BoostsTable').then(m => ({ default: m.BoostsTable })));

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ChartTooltip, Legend);

interface BoostDay {
  day: string;
  spend: number;
  impressions: number;
  reach: number | null;
  clicks: number;
  likes: number;
}

interface Boost {
  ad_id: string;
  network: 'meta' | 'linkedin';
  boosted_from: string;
  name: string | null;
  campaign: string | null;
  objective: string | null;
  status: string | null;
  currency: string;
  date_start: string | null;
  date_stop: string | null;
  permalink: string | null;
  post: { id: string; published_at: string; excerpt: string; text?: string } | null;
  spend: number;
  impressions: number;
  reach: number | null;
  frequency: number | null;
  clicks: number;
  link_clicks: number | null;
  likes: number;
  comments: number;
  shares: number;
  saves: number | null;
  follows: number | null;
  video_views: number | null;
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  cost_per_like: number | null;
  engagements: number;
  fetched_at: string | null;
  /** Where the money was delivered, per network — an Instagram boost often spends on Facebook too */
  delivery: { platform: string; spend: number; impressions: number; reach: number; clicks: number; likes: number }[];
  days: BoostDay[];
}

/** A boost row is tall once opened; a short page keeps the list scannable. */
const BOOSTS_PER_PAGE = 6;

const money = (n: number | null | undefined) => formatMoney(n);
const count = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString());
/** 10,712 → 10.7K, for the one-line summary on a phone */
const compact = (n: number | null | undefined) =>
  n == null ? '—' : new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
const shortDate = (iso: string | null) =>
  iso ? new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?';

/** Meta reports statuses in capitals (ACTIVE, PAUSED…); show them in words. */
const statusLabel = (s: string | null) => (s ? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') : null);

/**
 * Every boost, one row each, on every network.
 *
 * "From boosted posts" says what boosting added in total; this says which posts were boosted,
 * when, and what each boost bought — the question asked on 2026-10-17. Facebook and Instagram
 * boosts are fetched from Meta on the hourly refresh; LinkedIn's come from imported Campaign
 * Manager reports, so a LinkedIn boost appears once its report is imported.
 */
export const Boosts: React.FC<{ days: number; refreshKey?: number }> = ({ days, refreshKey }) => {
  const { token } = useAuth();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [boosts, setBoosts] = useState<Boost[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  /** 'all', or one network the boosted posts were on */
  const [network, setNetwork] = useState<string>('all');
  /** List for reading one boost at a time; Table for comparing them (desktop only) */
  const [view, setView] = useState<'list' | 'table'>('list');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/analytics/boosts?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setBoosts((await res.json()).boosts || []);
    } catch {
      // A missing boosts list must not take the rest of Stats down with it.
    }
  }, [days, token]);

  useEffect(() => { load(); }, [load, refreshKey]);
  // A new range or import starts from the newest boosts again
  useEffect(() => { setPage(0); }, [days, refreshKey, network]);

  if (boosts === null) return <SectionSkeleton rows={4} />;

  // Every network gets a button, boosted or not — a missing button read as a missing feature
  const networks = ['facebook', 'instagram', 'linkedin', 'twitter'];
  // A network's filter shows boosts of its posts AND boosts that spent money there: Facebook
  // spend usually comes from Instagram boosts Meta also delivered on Facebook
  const spentOn = (b: Boost, n: string) => b.delivery.find(d => d.platform === n)?.spend || 0;
  const filtered = network === 'all'
    ? boosts
    : boosts.filter(b => b.boosted_from === network || spentOn(b, network) > 0);
  const total = network === 'all'
    ? filtered.reduce((n, b) => n + b.spend, 0)
    : filtered.reduce((n, b) => n + spentOn(b, network), 0);
  const pages = Math.ceil(filtered.length / BOOSTS_PER_PAGE);
  const current = Math.min(page, Math.max(0, pages - 1));
  const page_ = filtered.slice(current * BOOSTS_PER_PAGE, (current + 1) * BOOSTS_PER_PAGE);

  /**
   * Under a network filter every row shows that network's share, so the rows add up to the
   * filter's total and to that network's line in Ad Spend By Network. A Facebook filter used to
   * list whole Instagram boosts — Instagram badge, Instagram spend — under a Facebook total.
   * Meta reports spend, impressions, clicks and likes per network; the rest exist only for the
   * whole boost and show a dash rather than a misleading whole-boost number.
   */
  const viewOf = (b: Boost): Boost & { share: boolean } => {
    if (network === 'all' || (b.network === 'linkedin' && b.boosted_from === network)) return { ...b, share: false };
    const d = b.delivery.find(x => x.platform === network);
    if (!d) return { ...b, share: false };
    if (b.delivery.length === 1) return { ...b, share: false };
    return {
      ...b,
      share: true,
      spend: d.spend,
      impressions: d.impressions,
      reach: null,
      frequency: null,
      clicks: d.clicks,
      link_clicks: null,
      likes: d.likes,
      comments: 0,
      shares: 0,
      saves: null,
      follows: null,
      video_views: null,
      ctr: d.impressions ? (d.clicks / d.impressions) * 100 : null,
      cpc: d.clicks ? d.spend / d.clicks : null,
      cpm: d.impressions ? (d.spend / d.impressions) * 1000 : null,
      cost_per_like: d.likes ? d.spend / d.likes : null,
      days: [],
    };
  };
  const shown = page_.map(viewOf);

  return (
    <SectionCard
      id="boosts"
      title="Boosts"
      actions={narrow ? (
        // Five buttons wrap awkwardly on a phone; a dropdown takes one line
        <TextField
          select
          size="small"
          label="Network"
          value={network}
          onChange={e => { setNetwork(e.target.value); setOpen(null); }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All networks</MenuItem>
          {networks.map(n => <MenuItem key={n} value={n}>{getPlatformName(n)}</MenuItem>)}
        </TextField>
      ) : (
        <>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={network}
            onChange={(_, v) => { if (v) { setNetwork(v); setOpen(null); } }}
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value="all">All</ToggleButton>
            {networks.map(n => (
              <ToggleButton key={n} value={n}>{getPlatformName(n)}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="list">List</ToggleButton>
            <ToggleButton value="table">Table</ToggleButton>
          </ToggleButtonGroup>
        </>
      )}
      description={<>
        {network === 'all'
          ? <>Every boost that ran in the last {days} days — {filtered.length} boost{filtered.length === 1 ? '' : 's'}, {money(total)} in all.</>
          : <>Boosts that ran on {getPlatformName(network)} in the last {days} days — {filtered.length} boost{filtered.length === 1 ? '' : 's'},{' '}
              {money(total)} spent on {getPlatformName(network)}. A boost bought on one Meta network is often shown on the other too.</>}
        {' '}Open one for everything it bought and a day-by-day chart.
      </>}
    >
      {filtered.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {network === 'twitter'
            ? 'ShoutVector cannot read X ad results yet: that needs access to X\'s separate Ads API. Boosted X posts will not appear here.'
            : network === 'linkedin'
              ? `No LinkedIn boosts in the last ${days} days. LinkedIn boosts appear once their Campaign Manager report is imported (Ad spend by network → Import LinkedIn boost report).`
              : network === 'all'
                ? `No boosts ran in the last ${days} days.`
                : `No boosts ran on ${getPlatformName(network)} in the last ${days} days.`}
        </Typography>
      )}

      {!narrow && view === 'table' && filtered.length > 0 && (
        <React.Suspense fallback={<SectionSkeleton frameless rows={6} />}>
        <BoostsTable rows={filtered.map(viewOf).map(b => ({
          key: `${b.network}-${b.ad_id}`,
          network_label: getPlatformName(b.share ? network : b.boosted_from),
          boosted_from: b.share ? network : b.boosted_from,
          title: b.post?.excerpt || b.name || b.campaign || `Ad ${b.ad_id}`,
          full_text: b.post?.text || b.post?.excerpt || b.name || undefined,
          permalink: b.permalink,
          date_start: b.date_start,
          date_stop: b.date_stop,
          status: b.status,
          currency: b.currency,
          spend: b.spend,
          impressions: b.impressions,
          reach: b.reach,
          clicks: b.clicks,
          ctr: b.ctr,
          cpc: b.cpc,
          cpm: b.cpm,
          likes: b.likes,
          comments: b.comments,
          shares: b.shares,
          saves: b.saves,
          follows: b.follows,
        }))} />
        </React.Suspense>
      )}

      {(narrow || view === 'list') && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {shown.map(b => {
          const key = `${b.network}-${b.ad_id}`;
          const expanded = open === key;
          return (
            <Box key={key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, cursor: 'pointer' }}
                onClick={() => setOpen(expanded ? null : key)}
              >
                {/* On a phone the badge sits on the dates line, so the title gets the full width */}
                {!narrow && (
                  <Chip
                    size="small"
                    label={getPlatformName(b.share ? network : b.boosted_from)}
                    sx={{ bgcolor: getPlatformColor(b.share ? network : b.boosted_from), color: '#fff', fontWeight: 600, flexShrink: 0 }}
                  />
                )}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {narrow && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={getPlatformName(b.share ? network : b.boosted_from)}
                        sx={{ bgcolor: getPlatformColor(b.share ? network : b.boosted_from), color: '#fff', fontWeight: 600, height: 20 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {shortDate(b.date_start)} – {shortDate(b.date_stop)}
                        {statusLabel(b.status) && ` · ${statusLabel(b.status)}`}
                      </Typography>
                    </Box>
                  )}
                  <FullTextTooltip text={b.post?.text || b.post?.excerpt || b.name}>
                    <Typography
                      variant="body2"
                      sx={narrow
                        // Two lines on a phone rather than a few truncated words
                        ? { fontWeight: 500, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                        : { fontWeight: 500 }}
                      noWrap={!narrow}
                    >
                      {b.post?.excerpt || b.name || b.campaign || `Ad ${b.ad_id}`}
                    </Typography>
                  </FullTextTooltip>
                  {narrow ? (
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                      <strong>{money(b.spend)}</strong>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' · '}{compact(b.impressions)} impressions · {compact(b.clicks)} clicks
                      </Typography>
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {shortDate(b.date_start)} – {shortDate(b.date_stop)}
                      {statusLabel(b.status) && ` · ${statusLabel(b.status)}`}
                      {' · '}<strong>{money(b.spend)}</strong>
                      {' · '}{count(b.impressions)} impressions
                      {' · '}{count(b.clicks)} clicks
                      {' · '}{count(b.likes)} {b.boosted_from === 'linkedin' ? 'reactions' : 'likes'}
                    </Typography>
                  )}
                  {b.share && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      The {getPlatformName(network)} part of a {money(boosts.find(x => x.ad_id === b.ad_id && x.network === b.network)?.spend)} boost
                      of {b.boosted_from === network ? 'this' : `${/^[aeiou]/i.test(getPlatformName(b.boosted_from)) ? 'an' : 'a'} ${getPlatformName(b.boosted_from)}`} post — Meta showed it on more than one network.
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" aria-label={expanded ? 'Hide details' : 'Show details'}>
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>

              <Collapse in={expanded} unmountOnExit>
                <Box
                  sx={{
                    display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                    gap: 1.5, mt: 1.5,
                  }}
                >
                  {[
                    ['Spent', money(b.spend)],
                    ['Impressions', count(b.impressions)],
                    ['People reached', count(b.reach)],
                    ['Frequency', b.frequency == null ? '—' : `${b.frequency.toFixed(2)}×`],
                    ['Clicks', count(b.clicks)],
                    ['Link clicks', count(b.link_clicks)],
                    ['Click-through rate', b.ctr == null ? '—' : `${b.ctr.toFixed(2)}%`],
                    ['Cost per click', money(b.cpc)],
                    ['Cost per 1,000 impressions', money(b.cpm)],
                    [b.boosted_from === 'linkedin' ? 'Reactions' : 'Likes', count(b.likes)],
                    ['Comments', count(b.comments)],
                    ['Shares', count(b.shares)],
                    ['Saves', count(b.saves)],
                    [b.boosted_from === 'facebook' ? 'New Page likes' : 'New followers', count(b.follows)],
                    ['Video views', count(b.video_views)],
                    [b.boosted_from === 'linkedin' ? 'Cost per reaction' : 'Cost per like', money(b.cost_per_like)],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Box>
                  ))}
                </Box>

                {b.delivery.length > 1 && (
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
                    <strong>Where the money went:</strong>{' '}
                    {b.delivery.filter(d => d.spend > 0).map(d =>
                      `${getPlatformName(d.platform)} ${money(d.spend)} (${count(d.impressions)} impressions)`).join(' · ')}
                  </Typography>
                )}

                {b.days.length > 1 && (
                  <Box sx={{ height: 220, mt: 2 }}>
                    <Bar
                      data={{
                        labels: b.days.map(d => shortDate(d.day)),
                        datasets: [
                          {
                            type: 'bar' as const,
                            label: 'Spent ($)',
                            data: b.days.map(d => d.spend),
                            backgroundColor: getPlatformColor(b.boosted_from),
                            yAxisID: 'spend',
                          },
                          {
                            type: 'line' as const,
                            label: 'Impressions',
                            data: b.days.map(d => d.impressions),
                            borderColor: '#888',
                            backgroundColor: '#888',
                            yAxisID: 'impressions',
                            tension: 0.3,
                          } as any,
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        scales: {
                          spend: { type: 'linear', position: 'left', beginAtZero: true, title: { display: true, text: '$' } },
                          impressions: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false } },
                        },
                      }}
                    />
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5, alignItems: 'center' }}>
                  {b.permalink && (
                    <Link href={b.permalink} target="_blank" rel="noopener noreferrer" underline="hover" variant="caption">
                      Open the post <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                    </Link>
                  )}
                  <Tooltip title={b.campaign || ''}>
                    <Typography variant="caption" color="text.secondary">
                      {b.network === 'linkedin' ? 'From the imported Campaign Manager report' : 'From Meta, refreshed hourly'}
                      {b.post ? '' : ' · not published through ShoutVector'}
                    </Typography>
                  </Tooltip>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      )}

      {/* Only when it earns its place, as on the post list below */}
      {(narrow || view === 'list') && pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Pagination
            count={pages}
            page={current + 1}
            onChange={(_, value) => {
              setPage(value - 1);
              setOpen(null);
              document.getElementById('boosts')?.scrollIntoView({ behavior: 'smooth' });
            }}
            color="primary"
            size="small"
            siblingCount={0}
          />
          <Typography variant="caption" color="text.secondary">
            {current * BOOSTS_PER_PAGE + 1}–{Math.min((current + 1) * BOOSTS_PER_PAGE, filtered.length)} of {filtered.length}
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
};
